import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { getGridNodeElement } from 'layout-dnd-utils/grids/render/domSelectors';
import { computeModuleGridAreaOnPegboard, isResizingPastMinimumDimensions, updatePegboardElementDom } from '../lib/domUtils/pegboardModuleDomUtils';
import { useDispatch, useSelector } from 'react-redux';
import { getTargetedDndAreaNodePositionsById } from '../redux/selectors/layoutSectionDndSelectors';
import { buildPegboardGridHoverPosition } from 'layout-dnd-utils/hoverPositions/gridLayoutTreeHoverPositionUtils';
import { getActiveBreakpointName } from 'ContentEditorUI/redux/selectors/styleBreakpointsSelectors';
import { isAutoPosition } from 'layout-data-lib/GridLayoutDataTree/typenameUtils';
import { setNodeStyles } from 'ContentEditorUI/redux/actions/gridsModuleActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const RESIZE_HANDLE_TYPES = {
  TOP: 'TOP',
  BOTTOM: 'BOTTOM',
  LEFT: 'LEFT',
  RIGHT: 'RIGHT',
  TOP_LEFT: 'TOP_LEFT',
  TOP_RIGHT: 'TOP_RIGHT',
  BOTTOM_LEFT: 'BOTTOM_LEFT',
  BOTTOM_RIGHT: 'BOTTOM_RIGHT'
};
const RESIZE_DOT_WIDTH = 8;
const BOTTOM_RIGHT_OFFSET = 5;
const TOP_LEFT_OFFSET = 3;
const StyledResizeDot = styled.div.withConfig({
  displayName: "GridLayoutResizeControls__StyledResizeDot"
})(({
  theme,
  $top,
  $left,
  $cursor
}) => {
  return {
    width: RESIZE_DOT_WIDTH,
    height: RESIZE_DOT_WIDTH,
    position: 'absolute',
    top: $top,
    left: $left,
    border: `1px solid ${theme.color['fill-info-default']}`,
    backgroundColor: theme.color['fill-surface-default'],
    zIndex: 2147483598,
    cursor: $cursor
  };
});
const ResizeDot = ({
  top,
  left,
  cursor,
  onMouseDown,
  type
}) => {
  return /*#__PURE__*/_jsx(StyledResizeDot, {
    $top: top,
    $left: left,
    $cursor: cursor,
    onMouseDown: e => {
      e.stopPropagation();
      onMouseDown(e, type);
    }
  });
};
const GridLayoutResizeControls = ({
  setContainerId,
  setGridLayoutId,
  selectedItemPositionInfo,
  selectedGridLayoutModuleNode
}) => {
  const dispatch = useDispatch();
  const activeBreakpoint = useSelector(getActiveBreakpointName);
  const nodePositionsById = useSelector(getTargetedDndAreaNodePositionsById);
  const selectedGridLayoutElement = getGridNodeElement(selectedGridLayoutModuleNode);
  const {
    top,
    bottom,
    left,
    right
  } = selectedItemPositionInfo;
  const [isDragging, setIsDragging] = useState(false);
  const draggingResizeHandle = useRef(null);
  const originalGridAreaRef = useRef(null);
  const lastUpdateGridArea = useRef(null);
  useEffect(() => {
    const globalMouseMoveListener = mouseEvent => {
      if (isDragging && draggingResizeHandle.current && originalGridAreaRef.current && selectedGridLayoutElement && nodePositionsById) {
        mouseEvent.preventDefault();
        mouseEvent.stopPropagation();
        const pegboardNodePosition = nodePositionsById[selectedGridLayoutModuleNode.getParentNodeId()];
        const adjustedMouseCoords = Object.assign({}, mouseEvent, {
          x: mouseEvent.x + window.scrollX,
          y: mouseEvent.y + window.scrollY
        });
        const pegboardHoverPosition = buildPegboardGridHoverPosition(pegboardNodePosition, adjustedMouseCoords);
        const {
          newRowStart,
          newColumnStart,
          newRowSpan,
          newColumnSpan
        } = computeModuleGridAreaOnPegboard(originalGridAreaRef.current, draggingResizeHandle.current, pegboardHoverPosition.hoveredRow, pegboardHoverPosition.hoveredColumn);
        updatePegboardElementDom(selectedGridLayoutElement, {
          rowStart: newRowStart,
          columnStart: newColumnStart,
          rowSpan: newRowSpan,
          columnSpan: newColumnSpan
        });
        if (lastUpdateGridArea.current && isResizingPastMinimumDimensions(pegboardNodePosition, selectedGridLayoutElement, {
          columnSpan: newColumnSpan,
          rowSpan: newRowSpan
        })) {
          updatePegboardElementDom(selectedGridLayoutElement, lastUpdateGridArea.current);
        } else {
          lastUpdateGridArea.current = {
            rowStart: newRowStart,
            rowSpan: newRowSpan,
            columnStart: newColumnStart,
            columnSpan: newColumnSpan
          };
        }
      }
    };
    const globalMouseUpListener = e => {
      if (isDragging) {
        e.stopPropagation();
        setIsDragging(false);
        if (setContainerId && setGridLayoutId) {
          setContainerId('');
          setGridLayoutId('');
        }
        if (lastUpdateGridArea.current) {
          const {
            rowStart: newRowStart,
            rowSpan: newRowSpan,
            columnStart: newColumnStart,
            columnSpan: newColumnSpan
          } = lastUpdateGridArea.current;
          dispatch(setNodeStyles(selectedGridLayoutModuleNode.getId(), selectedGridLayoutModuleNode.getRootId(), true, activeBreakpoint, {
            gridPosition: {
              type: 'MANUAL',
              rowStart: newRowStart,
              columnStart: newColumnStart
            },
            gridSpanning: {
              rowsSpanned: newRowSpan,
              columnsSpanned: newColumnSpan
            }
          }));
        }
        if (selectedGridLayoutElement) {
          selectedGridLayoutElement.style.removeProperty('grid-row');
          selectedGridLayoutElement.style.removeProperty('grid-column');
        }
      }
    };
    document.addEventListener('mousemove', globalMouseMoveListener);
    document.addEventListener('mouseup', globalMouseUpListener);
    return () => {
      document.removeEventListener('mousemove', globalMouseMoveListener);
      document.removeEventListener('mouseup', globalMouseUpListener);
    };
  }, [activeBreakpoint, dispatch, isDragging, nodePositionsById, selectedGridLayoutElement, selectedGridLayoutModuleNode, setContainerId, setGridLayoutId]);
  const onMouseDown = useCallback((e, selectedResizeHandle) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDragging(true);
    if (setContainerId && setGridLayoutId) {
      setContainerId(selectedGridLayoutModuleNode.getParentNodeId());
      setGridLayoutId(selectedGridLayoutModuleNode.getRootId());
    }
    draggingResizeHandle.current = selectedResizeHandle;
    const {
      gridPosition,
      gridSpanning
    } = selectedGridLayoutModuleNode.getEditorProvidedBreakpointStyles(activeBreakpoint);
    if (gridPosition && gridSpanning && !isAutoPosition(gridPosition)) {
      var _gridPosition$rowStar, _gridPosition$columnS, _gridSpanning$rowsSpa, _gridSpanning$columns;
      originalGridAreaRef.current = {
        rowStart: (_gridPosition$rowStar = gridPosition.rowStart) !== null && _gridPosition$rowStar !== void 0 ? _gridPosition$rowStar : 0,
        columnStart: (_gridPosition$columnS = gridPosition.columnStart) !== null && _gridPosition$columnS !== void 0 ? _gridPosition$columnS : 0,
        rowSpan: (_gridSpanning$rowsSpa = gridSpanning.rowsSpanned) !== null && _gridSpanning$rowsSpa !== void 0 ? _gridSpanning$rowsSpa : 0,
        columnSpan: (_gridSpanning$columns = gridSpanning.columnsSpanned) !== null && _gridSpanning$columns !== void 0 ? _gridSpanning$columns : 0
      };
    }
  }, [activeBreakpoint, selectedGridLayoutModuleNode, setContainerId, setGridLayoutId]);
  const verticalMiddle = top + (bottom - top) / 2;
  const horizontalMiddle = left + (right - left) / 2;
  if (!selectedGridLayoutModuleNode.getParentNode().isPegboardContainer()) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ResizeDot, {
      type: RESIZE_HANDLE_TYPES.TOP_LEFT,
      top: top - TOP_LEFT_OFFSET,
      left: left - TOP_LEFT_OFFSET,
      cursor: "nwse-resize",
      onMouseDown: onMouseDown
    }), /*#__PURE__*/_jsx(ResizeDot, {
      type: RESIZE_HANDLE_TYPES.TOP_RIGHT,
      top: top - TOP_LEFT_OFFSET,
      left: right - BOTTOM_RIGHT_OFFSET,
      cursor: "nesw-resize",
      onMouseDown: onMouseDown
    }), /*#__PURE__*/_jsx(ResizeDot, {
      type: RESIZE_HANDLE_TYPES.BOTTOM_LEFT,
      top: bottom - BOTTOM_RIGHT_OFFSET,
      left: left - TOP_LEFT_OFFSET,
      cursor: "nesw-resize",
      onMouseDown: onMouseDown
    }), /*#__PURE__*/_jsx(ResizeDot, {
      type: RESIZE_HANDLE_TYPES.BOTTOM_RIGHT,
      top: bottom - BOTTOM_RIGHT_OFFSET,
      left: right - BOTTOM_RIGHT_OFFSET,
      cursor: "nwse-resize",
      onMouseDown: onMouseDown
    }), /*#__PURE__*/_jsx(ResizeDot, {
      type: RESIZE_HANDLE_TYPES.LEFT,
      top: verticalMiddle - TOP_LEFT_OFFSET,
      left: left - TOP_LEFT_OFFSET,
      cursor: "col-resize",
      onMouseDown: onMouseDown
    }), /*#__PURE__*/_jsx(ResizeDot, {
      type: RESIZE_HANDLE_TYPES.RIGHT,
      top: verticalMiddle - TOP_LEFT_OFFSET,
      left: right - BOTTOM_RIGHT_OFFSET,
      cursor: "col-resize",
      onMouseDown: onMouseDown
    }), /*#__PURE__*/_jsx(ResizeDot, {
      type: RESIZE_HANDLE_TYPES.TOP,
      top: top - TOP_LEFT_OFFSET,
      left: horizontalMiddle - TOP_LEFT_OFFSET,
      cursor: "row-resize",
      onMouseDown: onMouseDown
    }), /*#__PURE__*/_jsx(ResizeDot, {
      type: RESIZE_HANDLE_TYPES.BOTTOM,
      top: bottom - BOTTOM_RIGHT_OFFSET,
      left: horizontalMiddle - TOP_LEFT_OFFSET,
      cursor: "row-resize",
      onMouseDown: onMouseDown
    })]
  });
};
export default GridLayoutResizeControls;